'use client';

/* oxlint-disable typescript/no-unsafe-type-assertion -- generic accumulators built key by key */
import { getTodayInDefaultTimeZone } from '@utils/helpers';
import { isNil, omitBy, pick } from 'es-toolkit';
import { useSearchParams } from 'next/navigation';
import type { Nullable, UrlKeys, Values } from 'nuqs';
import { type Options, useQueryStates } from 'nuqs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { normalizeDefaultState } from './default-state-normalization';
import type { UrlStateParserMap, UrlStateStorageOptions } from './merge-state-with-storage';
import { getAdapter, mergeStateWithStorage, rejectPastDatesInState } from './merge-state-with-storage';
import { persistUpdates } from './persist-updates';
import { useIsHydrated } from './use-is-hydrated';
export function useUrlStateStorage<Schema extends UrlStateParserMap>({
  schema,
  storage = {},
  urlKeys
}: UseUrlStateStorageOptions<Schema>): UseUrlStateStorageResult<Values<Schema>> {
  const [urlState, setUrlState] = useQueryStates(schema, {
    urlKeys
  });
  // Render-consistent search params. During client-side navigations the new
  // page tree renders before Next.js commits the URL to the browser history,
  // so window.location.search would still show the PREVIOUS page's query and
  // persisted cookie values would wrongly win over params of the target URL.
  const searchParams = useSearchParams();
  const isHydrated = useIsHydrated();
  const [isInitialized, setIsInitialized] = useState(false);
  const adapter = getAdapter(storage?.adapter);

  // Merges URL state with persisted storage values
  // Priority: URL state > storage values > default values
  const urlStateWithStorage = useMemo(() => {
    try {
      const defaultState = isInitialized ? urlState : normalizeDefaultState(storage?.defaultValue, schema);
      // Withholding the search params until hydration finished makes the
      // hydration render take the same branch as the server render, i.e. render
      // the server-resolved defaultValue verbatim instead of re-deriving state
      // from document.cookie and the browser clock. Those inputs do not exist
      // on the server, so using them during hydration produced mismatching
      // date/pax text (minified React error #418).
      const urlSearchParams = isHydrated ? searchParams : null;
      // nuqs state can be stale on the first render(s) after a client-side
      // navigation remounts this hook: its module-level cache still holds the
      // values of a previous visit until its own sync effect runs. Until this
      // provider is initialized, a server-provided defaultValue (resolved from
      // the actual request URL with URL > cookie > default precedence) is the
      // authoritative snapshot, so prefer it over nuqs for URL-present keys.
      const preferDefaultForUrlKeys = !isInitialized && !isNil(storage?.defaultValue);
      return mergeStateWithStorage({
        defaultState,
        urlState,
        schema,
        storage,
        urlSearchParams,
        preferDefaultForUrlKeys
      });
    } catch (error) {
      console.error('Failed to merge URL state with persistence:', error);
      return urlState;
    }
  }, [urlState, schema, isInitialized, storage, searchParams, isHydrated]);
  const updateUrlState = useCallback((updates: Partial<Nullable<Values<Schema>>>, options?: Options) => {
    void setUrlState(updates, options);
    persistUpdates(updates, storage?.persist, schema, adapter);
  }, [setUrlState, storage?.persist, adapter, schema]);

  // Initial sync: write merged state (URL > cookies > defaults) back to URL once
  // Waits for hydration so it syncs the storage-merged snapshot, not the
  // server-resolved one the hydration render is pinned to.
  useEffect(() => {
    if (!isHydrated || isInitialized || !storage?.initialUrlSync) return;
    const storageKeys = Array.isArray(storage?.initialUrlSync) ? storage?.initialUrlSync : storage?.persist;
    const today = getTodayInDefaultTimeZone();
    const initialUrlState = rejectPastDatesInState(pick(urlStateWithStorage, storageKeys ?? []), today);
    void setUrlState(initialUrlState, {
      history: 'replace'
    });

    // A nil here means "no value anywhere", not an explicit clear — filter it
    // out so persistUpdates writes values without removing stored ones.
    persistUpdates(omitBy(initialUrlState, isNil), storageKeys, schema, adapter);

    // oxlint-disable-next-line react/set-state-in-effect -- marks the one-time URL write-back as done
    setIsInitialized(true);
  }, [isHydrated, isInitialized, setUrlState, urlStateWithStorage, storage?.initialUrlSync, storage?.persist, adapter, schema]);

  // Without initialUrlSync there is no write-back step: consider the state
  // initialized after the first post-hydration effect pass (nuqs's own sync
  // effect has run by then — it is registered before this one), so a
  // server-provided defaultValue stops overriding live nuqs state (see
  // preferDefaultForUrlKeys).
  useEffect(() => {
    if (!isHydrated || isInitialized || storage?.initialUrlSync) return;

    // oxlint-disable-next-line react/set-state-in-effect -- must run after nuqs's own sync effect, see comment above
    setIsInitialized(true);
  }, [isHydrated, isInitialized, storage?.initialUrlSync]);
  return [urlStateWithStorage, updateUrlState, {
    isInitialized
  }];
}
export type UseUrlStateStorageOptions<Schema extends UrlStateParserMap> = {
  schema: Schema;
  storage?: UrlStateStorageOptions<Values<Schema>>;
  urlKeys?: UrlKeys<Schema>;
};
type UseUrlStateStorageResult<T extends Record<string, unknown>> = [T, (updates: Partial<Nullable<T>>, options?: Options) => void, {
  isInitialized: boolean;
}];